import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Snackbar,
  TextField,
  Typography,
  Backdrop,
  CircularProgress,
  Tooltip,
  Checkbox,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import ClearIcon from "@mui/icons-material/Clear";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { ProductRegistrationStyles as styles } from "./ProductRegistrationStyle";
import UserFilterSelect from "../components/UserFilter";
import { useProductRegistration } from "../hooks/logic/useProductRegistration";
import BaseFilterSelect from "../components/BaseFilter";
import ProductSetRow from "../components/ProductSetRow";
import NoteEditor from "../components/NoteEditor";
import CsvImportDialog from "../components/CsvImportDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import DeliveryUnitSelect from "../components/DeliveryUnitSelect";
import ListingsItemDialog from "../components/ListingsItemDialog";
import { CheckBox } from "@mui/icons-material";
import { canManageUsers, isGeneralUser, isNotOwner } from "../constants/roles";

const ProductRegistration = () => {
  const {
    loginUser,
    userOptions,
    baseOptions,
    handleUserChange,
    handleBaseChange,
    selectedDate,
    setSelectedDate,
    rows,
    rowDates,
    handleAddRow,
    handleDeleteRow,
    amazonItemName,
    setAmazonItemName,
    setCount,
    setSetCount,
    deliveryCount,
    setDeliveryCount,
    deliveryUnit,
    setDeliveryUnit,
    expectedCount,
    setExpectedCount,
    storageLocation,
    setStorageLocation,
    notes,
    setNotes,
    asin,
    setAsin,
    sku,
    setSku,
    fnsku,
    setFnsku,
    jan,
    setJan,
    selectedBaseId,
    setSelectedBaseId,
    selectedUserId,
    setSelectedUserId,
    productSetValues,
    handleProductSetValueChange,
    handleRegistItems,
    isRegisterLoading,
    toastOpen,
    toastMessage,
    toastSeverity,
    handleCloseToast,
    dialogOpen,
    setDialogOpen,
    confirmOpen,
    setConfirmOpen,
    importSuccessCount,
    setImportSuccessCount,
    importFailCount,
    setImportFailCount,
    setParsedData,
    handleFileDrop,
    handleOk,
    checkValidate,
    keyword,
    setKeyword,
    handleFbaSearch,
    isListingsItemsLoading,
    fbaDialogOpen,
    setFbaDialogOpen,
    listingsItems,
    setListingsItems,
    handleSelectListingsItem,
    syncItems,
    isSyncItemsLoading,
    syncDialog,
    setSyncDialog,
    syncItemsStatus,
    resetSyncDialog,
    setResetSyncDialog,
    handleResetSyncItems,
    isResetSyncItemsLoading,
    isNeedConfimationItem,
    setIsNeedConfimationItem,
    needConfimationWarningDialog,
    setNeedConfimationWarningDialog,
    handleOpenCsvImport,
    memoUserName,
    isApiLink,
    isLwaStatusChecked,
  } = useProductRegistration();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Box
        sx={{
          backgroundColor: "white",
          minHeight: "100vh",
          maxHeight: "100vh",
          overflowY: "auto",
        }}
      >
        <ConfirmDialog
          open={confirmOpen}
          title="確認"
          message={`取り込み：${importSuccessCount}件\n失敗：${importFailCount}件\n\n取り込みを実行しますか。`}
          buttons={[
            {
              label: "いいえ",
              variant: "text",
              onClick: () => setConfirmOpen(false),
            },
            {
              label: "はい",
              variant: "contained",
              onClick: () => {
                handleOk();
                setConfirmOpen(false);
              },
              disabled: importSuccessCount === 0,
            },
          ]}
        />
        <ListingsItemDialog
          open={fbaDialogOpen}
          onClose={() => setFbaDialogOpen(false)}
          listingsItems={listingsItems!}
          setListingsItems={setListingsItems}
          onSelect={handleSelectListingsItem}
          initialKeyword={keyword}
        />
        <ConfirmDialog
          open={syncDialog}
          title="確認"
          message={
            "同期中は商品情報の検索、CSVアップロードができなくなります。\nよろしいですか？\n\n※商品を追加した場合は、「検索」押下で追加可能です。\n※商品情報の変更、削除を行った場合に実施してください。\n※商品数が多いと同期時間も長くなります。"
          }
          buttons={[
            {
              label: "いいえ",
              variant: "text",
              onClick: () => setSyncDialog(false),
            },
            {
              label: "はい",
              variant: "contained",
              onClick: () => {
                syncItems();
                setSyncDialog(false);
              },
            },
          ]}
        />
        <ConfirmDialog
          open={resetSyncDialog}
          title="確認"
          message={
            "「同期中」の状態を解除します。\nよろしいですか？\n\n※同期が途中で中断され、いつまでも「同期中」のままになる場合にご利用ください。\n※実際に同期が動作している場合は、その同期は中断されます（データは壊れません。再度「同期」を実行してください）。"
          }
          buttons={[
            {
              label: "いいえ",
              variant: "text",
              onClick: () => setResetSyncDialog(false),
            },
            {
              label: "はい",
              variant: "contained",
              onClick: () => {
                handleResetSyncItems();
                setResetSyncDialog(false);
              },
            },
          ]}
        />

        <Box sx={styles.titleBar}>
          <Typography variant="h6" sx={styles.titleText}>
            商品情報
          </Typography>
        </Box>
        {/* Amazon未連携のときは常に警告を表示（本人のLWA連携状態） */}
        {isLwaStatusChecked && !isApiLink && (
          <Typography
            sx={{
              fontSize: 12,
              color: "error.main",
              px: 2,
              mb: 1,
            }}
          >
            Amazonと連携されていません。商品同期・プラン作成・配送ラベル印刷などの
            Amazon連携機能はご利用いただけません。ユーザー情報画面からAmazon連携を行ってください。
          </Typography>
        )}
        <Box sx={styles.buttonWrapper}>
          <Button
            variant="contained"
            sx={styles.amazonButton}
            onClick={() => window.open("https://www.amazon.co.jp/", "_blank")}
          >
            Amazonページ
          </Button>
          <Box sx={styles.fbaItem}>
            <TextField
              label={
                "登録する商品のキーワード、JANコード、ASINなどを入力してください"
              }
              size="small"
              sx={styles.keywordInput}
              InputLabelProps={{ sx: { fontSize: "0.9rem" } }}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              disabled={isListingsItemsLoading}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleFbaSearch();
                }
              }}
              InputProps={{
                endAdornment: keyword && (
                  <IconButton size="small" onClick={() => setKeyword("")}>
                    <ClearIcon />
                  </IconButton>
                ),
              }}
            />
            <Tooltip title="初回検索時は時間がかかります" arrow>
              <Button
                variant="contained"
                sx={styles.searchButton}
                onClick={() => handleFbaSearch()}
                disabled={
                  isListingsItemsLoading ||
                  isSyncItemsLoading ||
                  syncItemsStatus
                }
              >
                {syncItemsStatus ? "同期中" : "検索"}
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              sx={styles.syncButton}
              onClick={() => setSyncDialog(true)}
              disabled={
                isListingsItemsLoading || isSyncItemsLoading || syncItemsStatus
              }
            >
              {syncItemsStatus ? "同期中" : "同期"}
            </Button>
          </Box>
          <Button
            variant="contained"
            sx={styles.csvUploadButton}
            onClick={handleOpenCsvImport}
            disabled={isSyncItemsLoading || syncItemsStatus}
          >
            {syncItemsStatus ? "同期中" : "CSVアップロード"}
          </Button>
          <ConfirmDialog
            open={needConfimationWarningDialog}
            title="確認"
            message={
              "セラー要確認商品のチェックを外してから\nCSV取込を実行してください。\n\n※セラー要確認商品は手入力での登録でご利用ください。"
            }
            buttons={[
              {
                label: "閉じる",
                variant: "contained",
                onClick: () => setNeedConfimationWarningDialog(false),
              },
            ]}
          />
          <CsvImportDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            onFileDrop={handleFileDrop}
            selectedBaseId={selectedBaseId}
            setSelectedBaseId={setSelectedBaseId}
            selectedUserId={selectedUserId}
            setSelectedUserId={setSelectedUserId}
            // baseOptions={baseOptions}
            // userOptions={userOptions}
          />
        </Box>
        {/* 同期が中断されて「同期中」のまま解除されない場合の復旧手段 */}
        {syncItemsStatus && (
          <Box sx={styles.resetSyncNotice}>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              同期中は検索・同期・CSVアップロードがご利用いただけません。同期が終わらない場合は解除してください。
            </Typography>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              onClick={() => setResetSyncDialog(true)}
              disabled={isResetSyncItemsLoading}
            >
              同期中を解除
            </Button>
          </Box>
        )}
        {canManageUsers(loginUser?.permissionId) && (
          <Box ml={1}>
            <Checkbox
              defaultChecked={false}
              checked={!!isNeedConfimationItem}
              onChange={(e) => {
                setIsNeedConfimationItem(e.target.checked);
              }}
            />
            セラー要確認商品
          </Box>
        )}
        <Box display="flex" alignItems="center" gap={1} mb={4} px={2}>
          <TextField
            label="商品名"
            variant="outlined"
            size="small"
            sx={{ width: isGeneralUser(loginUser?.permissionId) ? "50%" : "40%" }}
            InputLabelProps={{
              sx: {
                fontSize: "0.9rem",
              },
            }}
            multiline
            value={amazonItemName}
            onChange={(e) => setAmazonItemName(e.target.value)}
          />
          <TextField
            label={"セット数"}
            size="small"
            sx={{ width: "10%" }}
            InputLabelProps={{
              sx: {
                fontSize: "0.9rem",
              },
            }}
            value={setCount}
            onChange={(e) => {
              const inputValue = e.target.value;
              if (/^\d*$/.test(inputValue)) {
                setSetCount(inputValue);
              }
            }}
          />
          <DeliveryUnitSelect
            value={deliveryUnit}
            onChange={setDeliveryUnit}
            sx={{ width: "15%" }}
          />
          <TextField
            label={"納品数"}
            size="small"
            sx={{ width: "10%" }}
            InputLabelProps={{
              sx: {
                fontSize: "0.9rem",
              },
            }}
            value={deliveryCount}
            onChange={(e) => {
              const inputValue = e.target.value;
              if (/^\d*$/.test(inputValue)) {
                setDeliveryCount(inputValue);
              }
            }}
          />
          <TextField
            label={"予定入荷総数"}
            size="small"
            sx={{
              width: "10%",
              backgroundColor: "#a0a0a0",
            }}
            InputLabelProps={{
              sx: {
                fontSize: "0.9rem",
              },
            }}
            value={expectedCount}
            onChange={(e) => setExpectedCount(String(e.target.value) || "")}
            disabled
          />
          {canManageUsers(loginUser?.permissionId) && (
            <DatePicker
              label="賞味期限"
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              minDate={new Date()}
              format="yyyy/MM/dd"
              slotProps={{
                textField: {
                  size: "small",
                  inputProps: {
                    readOnly: false,
                  },
                  InputLabelProps: {
                    sx: {
                      fontSize: "0.9rem",
                    },
                  },
                },
              }}
              sx={{
                width: "10%",
                backgroundColor: rows.length > 0 ? "#a0a0a0" : "inherit",
              }}
              disabled={rows.length > 0}
            />
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={1} mb={4} px={2}>
          <TextField
            label={"ASIN"}
            size="small"
            sx={{
              width: isGeneralUser(loginUser?.permissionId) ? "24%" : "15%",
              backgroundColor:
                isNotOwner(loginUser?.permissionId) && isApiLink
                  ? "#a0a0a0"
                  : "inherit",
            }}
            InputLabelProps={{ sx: { fontSize: "0.9rem" } }}
            value={asin}
            onChange={(e) => setAsin(e.target.value)}
            disabled={isNotOwner(loginUser?.permissionId) && isApiLink}
          />
          <TextField
            label={"SKU"}
            size="small"
            sx={{
              width: isGeneralUser(loginUser?.permissionId) ? "24%" : "15%",
              backgroundColor:
                isNotOwner(loginUser?.permissionId) && isApiLink
                  ? "#a0a0a0"
                  : "inherit",
            }}
            InputLabelProps={{ sx: { fontSize: "0.9rem" } }}
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            disabled={isNotOwner(loginUser?.permissionId) && isApiLink}
          />
          <TextField
            label={"FNSKU"}
            size="small"
            sx={{
              width: isGeneralUser(loginUser?.permissionId) ? "24%" : "15%",
              backgroundColor:
                isNotOwner(loginUser?.permissionId) && isApiLink
                  ? "#a0a0a0"
                  : "inherit",
            }}
            InputLabelProps={{ sx: { fontSize: "0.9rem" } }}
            value={fnsku}
            onChange={(e) => setFnsku(e.target.value)}
            disabled={isNotOwner(loginUser?.permissionId) && isApiLink}
          />
          <TextField
            label={"JAN"}
            size="small"
            sx={{
              width: isGeneralUser(loginUser?.permissionId) ? "24%" : "15%",
              backgroundColor: rows.length > 0 ? "#a0a0a0" : "inherit",
            }}
            InputLabelProps={{ sx: { fontSize: "0.9rem" } }}
            value={jan}
            onChange={(e) => setJan(e.target.value)}
            disabled={rows.length > 0}
          />
          <BaseFilterSelect
            options={baseOptions}
            value={selectedBaseId}
            onSelect={handleBaseChange}
            sx={{ width: "20%" }}
            labelFontSize="0.9rem"
          />

          {canManageUsers(loginUser?.permissionId) && (
            <>
              <UserFilterSelect
                options={userOptions}
                value={selectedUserId}
                onSelect={handleUserChange}
                sx={{ width: "20%" }}
                labelFontSize="0.9rem"
              />
            </>
          )}
        </Box>

        {rows.length > 0 && (
          <Box
            sx={{
              borderTop: "2px solid #999",
              my: 4,
              mx: 2,
            }}
          />
        )}

        {rows.map((id) => (
          <ProductSetRow
            key={id}
            id={id}
            values={
              productSetValues[id] ?? {
                jan: "",
                name: "",
                setCount: "",
                deliveryCount: deliveryCount,
                expectedCount: "",
                shippedCount: "",
                receivedCount: "",
                expiryDate: null,
              }
            }
            onChange={handleProductSetValueChange}
            onDelete={handleDeleteRow}
            isRegistration={true}
          />
        ))}

        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={1}
          mb={4}
          px={2}
        >
          <Button
            variant="contained"
            sx={styles.orangeButton}
            onClick={handleAddRow}
            disabled={isListingsItemsLoading}
          >
            セットを追加する
          </Button>
        </Box>
        <Box mt={16}>
          <NoteEditor
            userName={memoUserName}
            notes={notes}
            setNotes={setNotes}
            loginUser={loginUser}
          />
        </Box>
        <Button
          onClick={handleRegistItems}
          variant="contained"
          sx={styles.floatingRegisterButton}
          disabled={isRegisterLoading || isListingsItemsLoading}
        >
          {isRegisterLoading ? "登録中..." : "登録"}
        </Button>
        <Snackbar
          open={toastOpen}
          // 同期結果の案内（FBA対象0件など）は読めるよう長めに表示する
          autoHideDuration={toastSeverity === "warning" ? 15000 : 4000}
          onClose={handleCloseToast}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{ zIndex: 5000 }}
        >
          <MuiAlert
            onClose={handleCloseToast}
            severity={toastSeverity}
            sx={{ width: "100%" }}
            elevation={6}
            variant="filled"
          >
            <span dangerouslySetInnerHTML={{ __html: toastMessage }} />
          </MuiAlert>
        </Snackbar>
        <Backdrop
          open={isListingsItemsLoading}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 100,
            color: "#fff",
          }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    </LocalizationProvider>
  );
};

export default ProductRegistration;
